import _ from 'lodash';
import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useParams } from 'react-router';
import { Profile } from '../../../../Types';
import { CoachingStatsSummaryForPeriodExtended } from '../../../../Types/CoachingStats';
import {
  getTMPeriodsForNow,
  GetCoachingStatsResponse,
  PeriodType,
  CoachingStatsAssociateType,
  CoachingStatsAssociateStatus,
} from '@amzn/aet-coachingstats-kit';
import {
  getExtendedAssociateDetails,
  getTMCoachingStatsSummary,
} from '../../../../Util/CoachingStatsUtilsForTM';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Link from '@amzn/meridian/link';
import DataSource from '../../../../api';
import Naws from '../../../../api/Naws';
import DataState from '../../../../Types/DataState';
import useInspireOptions from '../../../../hooks/useInspireOptions';

import ExpandableCard, {
  ExpandableCardGroupProvider,
} from '../../../../commons/ExpandableCard';
import { FormattedMessage, useIntl } from 'react-intl';
import LinearGaugeWithSubtitle from '../../../../commons/LinearGaugeWithSubtitle';
import { useNavigate } from 'react-router';
import { renderUrl, urls } from '../../../../Util';
import { ModalProvider } from './ModalContext';
import UserBadgeRow from './UserBadgeRow';
import Icon from '@amzn/meridian/icon';
import tokens from '@amzn/meridian-tokens/base/icon/info-knockout';
import CoachingModal from './CoachingModal';
import { VerticalOption } from '@amzn/aet-galactus-kit';
import { getUserIdWithPrefix } from '../../../../Util/Helper';

const ICONS_PER_ROW = 6;

interface CoachingStatsContainerProps {
  state: string;
  profile: Profile;
}

const CoachingStatsContainer: React.FC<CoachingStatsContainerProps> = ({
  state,
  profile,
}: CoachingStatsContainerProps): React.ReactElement => {
  const apiNaws: typeof Naws = DataSource.getDataFeed('NAWS');
  const { userId: userIdFromUrl } = useParams() as {
    userId: string;
  };
  const { userID: requesterUserId, cscEntityPrefix } = useInspireOptions();
  const { directReports, managers } = profile;

  const userId = userIdFromUrl || requesterUserId;

  const [coachingStats, setCoachingStats] =
    useState<GetCoachingStatsResponse>();

  const [coachingStatsState, setCoachingStatsState] = useState(
    DataState.LOADING
  );

  // Make the following line order agnostic
  const tmPeriods = getTMPeriodsForNow();

  useEffect(() => {
    const userIdWithPrefix = `${cscEntityPrefix}:${userId}`;

    const queryCoachingStats = apiNaws.prepareQueryForGetCoachingStats(
      userIdWithPrefix,
      [tmPeriods.currentWeek, tmPeriods.currentMonth]
    );

    apiNaws.getCoachingStats(
      queryCoachingStats,
      setCoachingStats,
      setCoachingStatsState
    );
  }, [userId]);

  const { tmCoachingStats, inactiveAssociates, movedTeamAssociates } =
    useMemo(() => {
      if (_.size(managers) > 0 && directReports && coachingStats) {
        const directReportsMap = _.keyBy(directReports, item =>
          getUserIdWithPrefix(cscEntityPrefix, item.userID)
        );

        const tmCoachingStats = getTMCoachingStatsSummary(
          tmPeriods.currentWeek,
          tmPeriods.currentMonth,
          directReportsMap,
          coachingStats
        );

        const inactiveAssociates = getExtendedAssociateDetails(
          coachingStats?.coachingStatsForPeriods
            .find(item => item.period.periodType === PeriodType.MONTH)
            ?.associateDetails.filter(
              user =>
                user.associateType ===
                CoachingStatsAssociateType.NOT_ACTIVELY_HANDLING_CONTACTS
            ) ?? [],
          directReportsMap
        );

        const movedTeamAssociates = getExtendedAssociateDetails(
          coachingStats?.coachingStatsForPeriods
            .find(item => item.period.periodType === PeriodType.MONTH)
            ?.associateDetails.filter(
              user =>
                user.associateStatus === CoachingStatsAssociateStatus.NEW_TEAM
            ) ?? [],
          directReportsMap
        );

        return { tmCoachingStats, inactiveAssociates, movedTeamAssociates };
      }
      return {
        tmCoachingStats: undefined,
        inactiveAssociates: [],
        movedTeamAssociates: [],
      };
    }, [coachingStats, managers, directReports]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'weekly' | 'monthly'>('monthly');

  const openWeeklyModal = () => {
    setModalType('weekly');
    setModalOpen(true);
  };

  const openMonthlyModal = () => {
    setModalType('monthly');
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  // Get vertical directly
  const [userVertical, setUserVertical] = useState<VerticalOption>(
    VerticalOption.Total
  );
  const [userVerticalState, setUserVerticalState] = useState(DataState.LOADING);

  useEffect(() => {
    const variables = apiNaws.prepareOrgVerticalQuery(
      [`${cscEntityPrefix}:${userId}`],
      `${cscEntityPrefix}:${userId}`
    );
    apiNaws.getOrgVertical(variables, setUserVertical, setUserVerticalState);
  }, [userId]);

  return (
    <>
      <Column spacingInset='none none 500 none'>
        <Text type='h400'>
          <FormattedMessage
            id='cs_perfportal_coachings_and_reviews'
            defaultMessage='Coachings and Reviews'
          />
        </Text>
        {tmCoachingStats && (
          <Row widths='grid-3' alignmentVertical='top'>
            <ModalProvider>
              <ExpandableCardGroupProvider>
                <WeeklyCoachingsCard
                  coachingSummary={
                    tmCoachingStats.extendedWeeklyCoachingSummary
                  }
                  openModal={openWeeklyModal}
                />
                <MonthlyCoachingsCard
                  coachingSummary={
                    tmCoachingStats.extendedMonthlyCoachingSummary
                  }
                  openModal={openMonthlyModal}
                />
                <WeeklyContactReviewsCard
                  coachingSummary={
                    tmCoachingStats.extendedWeeklyCoachingSummary
                  }
                />
              </ExpandableCardGroupProvider>
            </ModalProvider>
          </Row>
        )}
      </Column>
      <CoachingModal
        open={modalOpen}
        onClose={closeModal}
        coachingType={modalType}
        vertical={userVertical}
        inactiveAssociates={inactiveAssociates}
        movedTeamsAssociates={movedTeamAssociates}
      />
    </>
  );
};

const WeeklyCoachingsCard = (props: {
  coachingSummary: CoachingStatsSummaryForPeriodExtended;
  openModal: () => void;
}) => {
  const {
    coachingSummary: {
      basicStats: { bottomQuartile, newHire, allAssociateTypes },
    },
  } = props;

  const intl = useIntl();

  const total = allAssociateTypes.totalCoachingRequired;
  const remaining = allAssociateTypes.remainingCoachings;
  const completed = total - remaining;
  // Bottom quartile data

  const bqTotal = bottomQuartile.total;
  const bqRemaining = bottomQuartile.remaining;
  const bqRemainingProfiles = bottomQuartile.remainingAssociateDetails;

  // New hire data

  const nhTotal = newHire.total;
  const nhRemaining = newHire.remaining;
  const newHireRemainingProfiles = newHire.remainingAssociateDetails;

  return (
    <ExpandableCard
      title={intl.formatMessage({
        id: 'cs_perfportal_coachings_this_week',
        defaultMessage: 'Coachings this week',
      })}
      header={
        <CoachingStatsCardHeading
          total={total}
          completed={completed}
          remaining={remaining}
        />
      }
      expandedContent={
        <Column spacing='300' spacingInset='none'>
          <Row alignmentHorizontal='justify'>
            <Text type='h50'>
              <FormattedMessage
                id='cs_perfportal_weekly_coachings_remaining'
                defaultMessage='({remaining}) Weekly coachings remaining'
                values={{ remaining }}
              />
            </Text>
            <div
              onClick={props.openModal}
              style={{ cursor: 'pointer', display: 'inline-block' }}
            >
              <Icon tokens={tokens} color='secondary' />
            </div>
          </Row>
          <Row alignmentHorizontal='justify'>
            <Text type='b200'>
              <FormattedMessage
                id='cs_perfportal_bq'
                defaultMessage='Bottom Quartile'
              />
            </Text>
            <Text type='b200'>
              <FormattedMessage
                id='cs_perfportal_remaining_of_total'
                defaultMessage='{remaining} / {total} remaining'
                values={{ remaining: bqRemaining, total: bqTotal }}
              />
            </Text>
          </Row>

          <UserBadgeRow
            profiles={bqRemainingProfiles}
            maxCount={ICONS_PER_ROW}
            modalTitle={intl.formatMessage({
              id: 'cs_perfportal_bq',
              defaultMessage: 'Bottom Quartile',
            })}
          />

          <Row alignmentHorizontal='justify'>
            <Text type='b200'>
              <FormattedMessage
                id='cs_perfportal_new_hire'
                defaultMessage='New Hire'
              />
            </Text>
            <Text type='b200'>
              <FormattedMessage
                id='cs_perfportal_remaining_of_total'
                defaultMessage='{remaining} / {total} remaining'
                values={{ remaining: nhRemaining, total: nhTotal }}
              />
            </Text>
          </Row>
          <UserBadgeRow
            profiles={newHireRemainingProfiles}
            maxCount={ICONS_PER_ROW}
            modalTitle={intl.formatMessage({
              id: 'cs_perfportal_new_hire',
              defaultMessage: 'New Hire',
            })}
          />
        </Column>
      }
      footer={<ViewAllCoaching />}
    />
  );
};

const MonthlyCoachingsCard = (props: {
  coachingSummary: CoachingStatsSummaryForPeriodExtended;
  openModal: () => void;
}) => {
  const intl = useIntl();

  const {
    coachingSummary: {
      basicStats: { allAssociateTypes },
    },
  } = props;

  const {
    remainingAssociateDetails,
    total,
    remaining,
    totalCoachingRequired,
    remainingCoachings,
  } = allAssociateTypes;

  const completed = totalCoachingRequired - remainingCoachings;

  return (
    <ExpandableCard
      title={intl.formatMessage({
        id: 'cs_perfportal_coachings_this_month',
        defaultMessage: 'Coachings this month',
      })}
      header={
        <CoachingStatsCardHeading
          total={totalCoachingRequired}
          completed={completed}
          remaining={remainingCoachings}
        />
      }
      expandedContent={
        <Column spacing='300' spacingInset='none'>
          <Row alignmentHorizontal='justify'>
            <Text type='h50'>
              <FormattedMessage
                id='cs_perfportal_monthly_coachings_remaining'
                defaultMessage='({remaining}) Monthly coachings remaining'
                values={{ remaining }}
              />
            </Text>
            <div
              onClick={props.openModal}
              style={{ cursor: 'pointer', display: 'inline-block' }}
            >
              <Icon tokens={tokens} color='secondary' />
            </div>
          </Row>
          <Row alignmentHorizontal='justify'>
            <Text type='b200'>
              <FormattedMessage
                id='cs_perfportal_associates'
                defaultMessage='Associates'
              />
            </Text>
            <Text type='b200'>
              <FormattedMessage
                id='cs_perfportal_remaining_of_total'
                defaultMessage='{remaining} / {total} remaining'
                values={{ remaining, total }}
              />
            </Text>
          </Row>
          <UserBadgeRow
            profiles={remainingAssociateDetails.filter(
              associates =>
                associates.associateStatus ===
                CoachingStatsAssociateStatus.REGULAR
            )}
            maxCount={ICONS_PER_ROW * 2}
            modalTitle={intl.formatMessage({
              id: 'cs_perfportal_associates',
              defaultMessage: 'Associates',
            })}
          />
        </Column>
      }
      footer={<ViewAllCoaching />}
    />
  );
};

const WeeklyContactReviewsCard = (props: {
  coachingSummary: CoachingStatsSummaryForPeriodExtended;
}) => {
  const intl = useIntl();

  const {
    coachingSummary: {
      basicStats: { contactReviewForAllAssociatesOverPeriod },
    },
  } = props;
  const navigate = useNavigate();

  const reviewed = contactReviewForAllAssociatesOverPeriod.total;
  const unopened = contactReviewForAllAssociatesOverPeriod.remaining;
  const remainingAssociateDetails =
    contactReviewForAllAssociatesOverPeriod.remainingAssociateDetails;
  const remainingUsernameCount = remainingAssociateDetails.length;

  return (
    <ExpandableCard
      title={intl.formatMessage({
        id: 'cs_perfportal_contact_reviews_this_week',
        defaultMessage: 'Contact Reviews this week',
      })}
      header={
        <ContactReviewCardHeading reviewed={reviewed} unopened={unopened} />
      }
      expandedContent={
        <>
          <Column spacing='300' spacingInset='none'>
            <Box>
              <Text type='h50'>
                <FormattedMessage
                  id='cs_perfportal_associates_unopened_reviews'
                  defaultMessage='({remaining}) Associates with unopened reviews'
                  values={{ remaining: remainingUsernameCount }}
                />
              </Text>
              <Text type='b200'>
                <FormattedMessage
                  id='cs_perfportal_remind_read_review'
                  defaultMessage='(Remind them to read review)'
                />
              </Text>
            </Box>
            <Row wrap='down' spacing='200 200'>
              <UserBadgeRow
                profiles={remainingAssociateDetails}
                maxCount={ICONS_PER_ROW}
                modalTitle={intl.formatMessage({
                  id: 'cs_perfportal_associates',
                  defaultMessage: 'Associates',
                })}
              />
            </Row>
          </Column>
        </>
      }
      footer={
        <Link onClick={navigate} href={renderUrl(urls.team)}>
          <FormattedMessage
            id='cs_perfportal_view_associates'
            defaultMessage='View associates'
          />
        </Link>
      }
    />
  );
};

interface CoachingStatsCardHeadingProps {
  total: number;
  completed: number;
  remaining: number;
}
const CoachingStatsCardHeading = ({
  total,
  completed,
  remaining,
}: CoachingStatsCardHeadingProps) => {
  const intl = useIntl();
  return (
    <Row alignmentVertical='bottom' spacingInset='none none 300 none'>
      <Row width='50%' spacing='200' alignmentVertical='baseline'>
        <Text type='h500'> {remaining} </Text>
        <Text type='h50' color='secondary'>
          <FormattedMessage
            id='cs_perfportal_coaching_remaining'
            defaultMessage='remaining'
          />
        </Text>
      </Row>
      <Row width='50%'>
        <LinearGaugeWithSubtitle
          total={total}
          value={completed}
          subtitle={intl.formatMessage(
            {
              id: 'cs_perfportal_coaching_completed',
              defaultMessage: '{completed} / {total} completed',
            },
            { completed, total }
          )}
        />
      </Row>
    </Row>
  );
};

interface ContactReviewCardHeadingProps {
  reviewed: number;
  unopened: number;
}

const ContactReviewCardHeading = ({
  reviewed,
  unopened,
}: ContactReviewCardHeadingProps) => {
  return (
    <Row alignmentVertical='bottom'>
      <Row width='50%' spacing='200' alignmentVertical='baseline'>
        <Text type='h500' data-testid='contact-review-reviewed-count'>
          {' '}
          {reviewed}{' '}
        </Text>
        <Text
          type='h50'
          color='secondary'
          data-testid='contact-review-reviewed-label'
        >
          <FormattedMessage
            id='cs_perfportal_contact_review_reviewed'
            defaultMessage='reviewed'
          />
        </Text>
      </Row>
      <Row width='50%' spacing='200' alignmentVertical='baseline'>
        <Text type='h500' data-testid='contact-review-unopened-count'>
          {' '}
          {unopened}{' '}
        </Text>
        <Text
          type='h50'
          color='secondary'
          data-testid='contact-review-unopened-label'
        >
          <FormattedMessage
            id='cs_perfportal_contact_review_unopened'
            defaultMessage='unopened'
          />
        </Text>
      </Row>
    </Row>
  );
};

const ViewAllCoaching = () => {
  const navigate = useNavigate();

  return (
    <Link
      onClick={navigate}
      href={renderUrl(urls.team, { params: { filter: 'coachings' } })}
    >
      <FormattedMessage
        id='cs_perfportal_view_all_coachings'
        defaultMessage='View all coachings'
      />
    </Link>
  );
};

export default CoachingStatsContainer;
